import { Directive, ElementRef, HostListener, OnDestroy } from '@angular/core';
import { NgControl } from '@angular/forms';
import {
  CultureKeyType,
  CULTURE_ROUTE_PARAM,
  FALLBACK_CULTURE,
} from '@constants/app.constants';
import { DATE_FORMAT_DEFAULT } from '@constants/date-format.constants';
import {
  DECIMAL_CHARACTER_REGEX,
  INVALID_DATE_KEY,
  OVERWRITEABLE_DATE_MASK_CHARACTERS_REGEX,
} from '@constants/form.constants';
import { NullableString } from '@models/app.models';
import { Store } from '@ngrx/store';
import {
  selectCurrentCultureDateDisplayFormat,
  selectCurrentCultureDateFormat,
} from '@selectors/culture.selector';
import { selectRouteParam } from '@selectors/router.selector';
import { DateTime } from 'luxon';
import { combineLatest, Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

@Directive({
  selector: '[appDateInput]',
  standalone: true,
})
export class DateInputDirective implements OnDestroy {
  destroy$ = new Subject<void>();
  format$ = this.store.select(selectCurrentCultureDateFormat);
  displayFormat$ = this.store.select(selectCurrentCultureDateDisplayFormat);
  culture$ = this.store.select(
    selectRouteParam(CULTURE_ROUTE_PARAM),
  ) as Observable<CultureKeyType | undefined>;

  displayFormat: NullableString = null;
  format: NullableString = null;
  culture: CultureKeyType = FALLBACK_CULTURE;
  buffer: string[] | null = null;
  nativeElement: HTMLInputElement | null = null;

  constructor(
    private el: ElementRef,
    private store: Store,
    public control: NgControl,
  ) {
    if (this.el.nativeElement instanceof HTMLInputElement) {
      this.nativeElement = this.el.nativeElement;
      this.nativeElement.autocomplete = 'off';
      combineLatest([this.format$, this.displayFormat$])
        .pipe(
          tap(([format, displayFormat]) => {
            // set our format from the store,
            // our buffer is initially the characters
            // from the format string (our mask)
            this.format = format;
            this.displayFormat = displayFormat;
            this.buffer = displayFormat.split('');
          }),
          takeUntil(this.destroy$),
        )
        .subscribe();

      this.culture$
        .pipe(
          tap((culture) => {
            this.culture = culture ?? FALLBACK_CULTURE;
          }),
          takeUntil(this.destroy$),
        )
        .subscribe();
      return;
    }

    console.error('appDateInput directive used on unsupported type');
  }

  @HostListener('blur', ['$event']) onBlur($event?: FocusEvent): void {
    if (!this.format || !this.nativeElement) {
      console.warn('No input/format provided for date mask directive (blur)');
      return;
    }

    $event?.preventDefault();
    $event?.stopPropagation();

    const inputValue = this.nativeElement.value;
    if (!inputValue || inputValue === this.displayFormat) {
      this.control.control?.setValue(null);
      return;
    }

    // date is already in a valid format (triggering validation manually probably)
    const dateTimeAlreadyFormatted = DateTime.fromFormat(
      inputValue,
      DATE_FORMAT_DEFAULT,
      {
        locale: this.culture,
      },
    );
    if (dateTimeAlreadyFormatted.isValid) {
      return;
    }
    const dateTime = DateTime.fromFormat(inputValue, this.format, {
      locale: this.culture,
    });
    if (!dateTime.isValid) {
      this.control.control?.setErrors({
        [INVALID_DATE_KEY]: {
          ctrlDate: dateTime,
        },
      });
      return;
    }

    // our date time is valid given the current format,
    // so we parse it to the default format and display
    // this to the user
    const dateStr = dateTime.toFormat(DATE_FORMAT_DEFAULT, {
      locale: this.culture,
    });
    this.triggerChange(dateStr);
    this.control.control?.setValue(dateStr);
  }

  @HostListener('focus', ['$event'])
  @HostListener('click', ['$event'])
  onFocusOrClick($event: FocusEvent | MouseEvent): void {
    if (!this.format || !this.nativeElement || !this.displayFormat) {
      console.warn('No input/format provided for date mask directive (focus)');
      return;
    }

    $event.preventDefault();
    $event.stopPropagation();

    // if the input has not yet had a value set
    // then on focus we will show the mask
    // and set the selection to the first character
    const inputValue = this.nativeElement.value;
    if (!inputValue) {
      this.triggerChange(this.displayFormat, 0, 1);
      return;
    }

    // if there is a value set, we will try to parse it out
    // using the default format, since this value was set on blur
    const dateTime = DateTime.fromFormat(inputValue, DATE_FORMAT_DEFAULT, {
      locale: this.culture,
    });
    if (!dateTime.isValid) {
      return;
    }

    // if the date was parsed successfully, we will convert it
    // back to the original format so that the user can edit it.
    this.triggerChange(
      dateTime.toFormat(this.format, {
        locale: this.culture,
      }),
      0,
      1,
    );
  }

  /**
   * Updates the mask according to the user input
   * @param $event The browser event
   * @param isTrueEvent Whether or not the event was actually triggered from the browser, or triggered manually
   * @returns Nothing
   */
  @HostListener('input', ['$event']) onInput(
    $event: InputEvent,
    isTrueEvent = true,
  ): void {
    $event.preventDefault();
    $event.stopPropagation();

    // For Samsung mobile only specifically when predictive seach ON insertEventTypeName becomes 'insertCompositionText'!
    // We should not code below the HTML DOM abstraction layer but we did it for a reason, so we have to face these variances!
    const Insert_Event_Input_Type = 'insertText';
    const Insert_Event_Input_Type_Samsung = 'insertCompositionText';
    // Pray GOD this name won't change in future releases of Samsung, for now its working !
    const Delete_Event_Input_Type = 'deleteContentBackward';

    if (
      !this.buffer ||
      !this.nativeElement ||
      !this.format ||
      !this.displayFormat
    ) {
      console.warn('No buffer/input/format provided for date mask directive');
      return;
    }

    let oldStart = this.nativeElement.selectionStart ?? 0;

    if (
      isTrueEvent &&
      ($event.inputType === Insert_Event_Input_Type ||
        $event.inputType === Insert_Event_Input_Type_Samsung)
    ) {
      --oldStart;
    }

    if (oldStart >= this.format.length) {
      // set back to previous value (don't allow entering text
      // when at end of mask)
      this.triggerChangeWithBufferValue(
        this.format.length - 1,
        this.format.length,
      );
      return;
    }

    const currentChar = this.buffer[oldStart] ?? '';
    const nextChar = this.buffer[oldStart + 1] ?? '';
    const previousChar = this.buffer[oldStart - 1] ?? '';

    // backspace
    if ($event.inputType === Delete_Event_Input_Type) {
      // replace current character with the same character in
      // our format string, effectively resetting it.
      this.buffer[oldStart] = this.displayFormat.charAt(oldStart);

      // if the previous character is not editable, skip it
      // in the cursor position.
      if (!OVERWRITEABLE_DATE_MASK_CHARACTERS_REGEX.exec(previousChar)) {
        --oldStart;
      }

      // selection range can't be less than 0->1
      this.triggerChangeWithBufferValue(
        Math.max(0, oldStart - 1),
        Math.max(1, oldStart),
      );
      return;
    }

    if (
      ($event.inputType !== Insert_Event_Input_Type &&
        $event.inputType !== Insert_Event_Input_Type_Samsung) ||
      !DECIMAL_CHARACTER_REGEX.exec($event.data ?? '')
    ) {
      // selection range is limited on the upper end by
      // the length of our mask (format string)
      this.triggerChangeWithBufferValue(
        Math.min(oldStart, this.format.length - 1),
        Math.min(oldStart + 1, this.format.length),
      );
      return;
    }

    if (oldStart >= this.format.length) {
      return;
    }

    if (!OVERWRITEABLE_DATE_MASK_CHARACTERS_REGEX.exec(currentChar)) {
      ++oldStart;
    }

    if (
      $event.inputType === Insert_Event_Input_Type_Samsung &&
      $event.data &&
      $event.data.length > 1
    ) {
      // SAMSUNG only Fix: For Samsung mobile 4th digit comes back as 2 digits (currently typed digit + previous digit!).
      // For example if I start typing '12/12/YYYY' in Samsung it will display like '12/112/YYYY'
      // $event.data value carries 12 instead of 2 upon typing the 4th character !!!
      // For now this bandaid is enought to handle Samsung show stopper, I was able to enter DOB and reach till payment screen on Samsung mobile with this fix.
      // TODO: Dig more why $event.data combines the previous digit exactly at 4th digit instead of just carries the typed digit.
      this.buffer[oldStart] = $event.data.charAt($event.data.length - 1);
    } else {
      this.buffer[oldStart] = $event.data ?? ' ';
    }

    // next char is not a placeholder character like m, d, y;
    // so move the caret up one more position (skip the char).
    if (!OVERWRITEABLE_DATE_MASK_CHARACTERS_REGEX.exec(nextChar)) {
      ++oldStart;
    }

    // selection range is limited on the upper end by
    // the length of our mask (format string)
    this.triggerChangeWithBufferValue(
      Math.min(oldStart + 1, this.format.length - 1),
      Math.min(oldStart + 2, this.format.length),
    );
  }

  setSelectionRange(start: number, end: number): void {
    if (!this.nativeElement) {
      return;
    }

    this.nativeElement.setSelectionRange(start, end);
  }

  triggerChangeWithBufferValue(
    setSelectionStart?: number,
    setSelectionEnd?: number,
  ): void {
    if (!this.buffer) {
      console.warn(
        'No buffer provided for date mask directive (trigger change with buffer)',
      );
      return;
    }
    this.triggerChange(
      this.buffer.join(''),
      setSelectionStart,
      setSelectionEnd,
    );
  }

  triggerChange(
    text: string,
    setSelectionStart?: number,
    setSelectionEnd?: number,
  ): void {
    if (!this.nativeElement) {
      console.warn(
        'No input provided for date mask directive (trigger change)',
      );
      return;
    }
    this.nativeElement.value = text;

    // set selection must be ran after the value is set to prevent the cursor
    // being automatically placed at the end
    if (setSelectionStart !== undefined && setSelectionEnd !== undefined) {
      setTimeout(() => {
        this.setSelectionRange(setSelectionStart, setSelectionEnd);
      }, 0);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }
}
